import { Box, Card, Grid, Skeleton, Tab, Tabs } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import moment from "moment";
import { scrollToElementTop } from "shared/utils/helpers";
import CustomButton from "shared/components/customButton";
import {
  allMouService,
  getSingleMOUService,
  rejectMOUService,
  signatureMOUService,
} from "shared/services/projectHeadService";
import { createMOUService } from "shared/services/adminService";
import { toastMessage } from "shared/components/toast";
import { useLocation, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { MOUSchema } from "shared/utils/validationSchemas";
import Loader from "shared/components/loader/animation";
import Confirmation from "shared/modals/confirmation";
import { Icons } from "assets";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";

const MOU_STATUS = [
  { label: "Draft", value: 0 },
  { label: "Review", value: 1 },
  { label: "Pending", value: 2 },
  { label: "Accepted", value: 3 },
  { label: "Declined", value: 4 },
  { label: "Rejected", value: 5 },
  { label: "Completed", value: 7 },
];

function statusClass(status: number): string {
  const classes: Record<number, string> = {
    3: "agreed",
    7: "agreed",
    4: "declined",
  };
  return classes[status] || "pending";
}

function PhNewAllMouSignature() {
  const { id } = useParams<{ id: string }>() as { id: string };
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [allMous, setAllMous] = useState<any[]>([]);
  const [mouLoader, setMouLoader] = useState<boolean>(true);
  const [selectedMOU, setSelectedMOU] = useState<any>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const [mouDetails, setMouDetails] = useState<any>(null);
  const [singleLoader, setSingleLoader] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [openConfirm, setOpenConfirmation] = useState<boolean>(false);
  const [action, setAction] = useState<any>({ label: "", type: "" });

  // ── Create form ───────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      description: "",
      freeBooksLimit: "",
      premiumBooksLimit: "",
      platformFee: "",
    },
    validationSchema: MOUSchema,
    onSubmit: (values) => {
      setLoader(true);
      const payload = {
        user_id: Number(id),
        agreement: values.description,
        free_book_ratio: values.freeBooksLimit,
        total_books: values.premiumBooksLimit,
        platform_commission: values.platformFee,
      };
      createMOUService(payload)
        .then(({ data }: any) => {
          toastMessage("success", data?.message);
          setLoader(false);
          setIsCreating(false);
          formik.resetForm();
          getAllMous();
        })
        .catch((err: any) => {
          toastMessage("error", err?.response?.data?.message);
          setLoader(false);
        });
    },
  });

  // ── Data fetching ─────────────────────────────────────────────────────────
  const getAllMous = (keepSelected = false) => {
    setMouLoader(true);
    allMouService(id)
      .then(({ data }) => {
        setMouLoader(false);
        const list: any[] = data?.data ?? [];
        setAllMous(list);
        if (keepSelected && selectedMOU) {
          const refreshed =
            list.find((m: any) => m.id === selectedMOU.id) ??
            list[0] ??
            null;
          setSelectedMOU(refreshed);
        } else if (list.length && !isCreating) {
          setSelectedMOU(list[0]);
        }
      })
      .catch((err) => {
        setMouLoader(false);
        toastMessage("error", err?.response?.data?.message);
      });
  };

  const getMOU = () => {
    setSingleLoader(true);
    getSingleMOUService(selectedMOU.id)
      .then(({ data }) => {
        setSingleLoader(false);
        setMouDetails(data?.data);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setSingleLoader(false);
      });
  };

  useEffect(() => {
    getAllMous();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedMOU) getMOU();
    // eslint-disable-next-line
  }, [selectedMOU]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
    setAction({ label: "", type: "" });
  };

  const handleActions = (actionObj: any) => {
    if (actionObj.type === "reject") {
      setLoader(true);
      rejectMOUService(selectedMOU?.id)
        .then(({ data }) => {
          toastMessage("success", data?.message);
          setLoader(false);
          getAllMous();
          handleCloseConfirmation();
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message);
          setLoader(false);
        });
    }
  };

  const handleApprove = () => {
    setLoader(true);
    const payload: any = {
      mou_id: selectedMOU?.id,
      agreement: mouDetails?.agreement,
      free_book_ratio: mouDetails?.free_book_ratio,
      total_books: mouDetails?.total_books,
      platform_commission: mouDetails?.platform_commission,
    };
    const formBody = new FormData();
    Object.keys(payload).forEach((key) => formBody.append(key, payload[key]));
    signatureMOUService(formBody)
      .then(({ data }: any) => {
        toastMessage("success", data?.message);
        setLoader(false);
        getAllMous(true);
      })
      .catch((err: any) => {
        toastMessage("error", err?.response?.data?.message);
        setLoader(false);
      });
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setActiveTab(0);
    setSelectedMOU(null);
    setMouDetails(null);
    formik.resetForm();
  };

  // ── Tab filtering ─────────────────────────────────────────────────────────
  const filteredMOUs = allMous.filter((mou) => {
    if (activeTab === 1) return mou.status === 2;
    if (activeTab === 2) return mou.status === 1;
    return true;
  });

  const handleTabChange = (_: React.SyntheticEvent, val: number) => {
    setActiveTab(val);
    setIsCreating(false);
    setMouDetails(null);
    const nextFiltered = allMous.filter((mou: any) => {
      if (val === 1) return mou.status === 2;
      if (val === 2) return mou.status === 1;
      return true;
    });
    setSelectedMOU(nextFiltered[0] ?? null);
  };

  useBreadcrumbs(
    {
      title: location?.state?.user?.full_name,
      path: routeConstant.phNewAllMouSignature.path.replace(":id", id),
    },
    true,
    location?.state
  );

  const selectedInView = filteredMOUs.some((m: any) => m.id === selectedMOU?.id);
  const showRightPanel = isCreating || selectedInView;

  return (
    <div>
      {/* ── Page header ───────────────────────────────────────────────────── */}
      <Box className={styles.pageHeader}>
        <div className={styles.pageTitle}>MOU List</div>
        <CustomButton
          text="Create New"
          buttonStyle={styles.createBtn}
          handleClick={handleCreateNew}
        />
      </Box>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <Box className={styles.tabsWrapper}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ style: { backgroundColor: "#9a469b" } }}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: "Poppins",
              fontSize: "14px",
              color: "#6c757d",
            },
            "& .Mui-selected": {
              color: "#9a469b !important",
              fontWeight: "600",
            },
          }}
        >
          <Tab label="All" />
          <Tab label="Pending MOU" />
          <Tab label="MOU from product manager" />
        </Tabs>
      </Box>

      {/* ── Full-width no data ────────────────────────────────────────────── */}
      {!mouLoader && filteredMOUs.length === 0 && !isCreating && (
        <div className={styles.noDataFullScreen}>
          <Icons.NoDataIcon />
          <h5>No Data Found</h5>
        </div>
      )}

      <Grid container spacing={2}>
        {/* ── Left: MOU list ───────────────────────────────────────────────── */}
        <Grid item xs={12} md={3}>
          {mouLoader ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width="100%"
                height="120px"
                sx={{ borderRadius: "10px", marginBottom: "16px" }}
              />
            ))
          ) : (
            filteredMOUs.map((item: any, inx: number) => (
              <div
                key={inx}
                className={
                  item?.id === selectedMOU?.id && !isCreating
                    ? styles.activeMOU
                    : styles.singleMOU
                }
                onClick={() => {
                  setIsCreating(false);
                  setSelectedMOU(item);
                  scrollToElementTop(detailRef.current);
                }}
              >
                <div className={styles[statusClass(item.status)]}>
                  {MOU_STATUS.find((s) => s.value === item.status)?.label}
                </div>
                <div className={styles.mouCardTitle}>MOU {inx + 1}</div>
              </div>
            ))
          )}
        </Grid>

        {/* ── Right panel ──────────────────────────────────────────────────── */}
        <Grid item xs={12} md={9} sx={!showRightPanel ? { display: "none" } : {}} ref={detailRef}>

          {/* ── Create form ──────────────────────────────────────────────── */}
          {isCreating && (
            <Card className={styles.detailCard}>
              <div className={styles.detailHeader}>
                <div>
                  <div className={styles.detailTitle}>Create MOU</div>
                  <div className={styles.detailSubTitle}>New MOU Contract</div>
                </div>
                <div className={styles.detailDate}>
                  Date: {moment().format("D/M/YYYY")}
                </div>
              </div>

              <form onSubmit={formik.handleSubmit}>
                <div className={styles.mouHeading}>
                  Agreement Description <span>*</span>
                </div>
                <textarea
                  className={styles.textarea}
                  id="description"
                  name="description"
                  placeholder="Enter agreement description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className={styles.errorText}>
                    {formik.errors.description}
                  </div>
                )}

                <Grid container sx={{ marginTop: "20px" }}>
                  <Grid xs={4}>
                    <div className={styles.titleHead}>Free Books Limit</div>
                    <input
                      className={styles.input}
                      id="freeBooksLimit"
                      name="freeBooksLimit"
                      type="text"
                      placeholder="Enter free books limit"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.freeBooksLimit}
                    />
                    {formik.touched.freeBooksLimit &&
                      formik.errors.freeBooksLimit && (
                        <div className={styles.errorText}>
                          {formik.errors.freeBooksLimit}
                        </div>
                      )}
                  </Grid>

                  <Grid xs={4}>
                    <div className={styles.titleHead}>Premium Books Limit</div>
                    <input
                      className={styles.input}
                      id="premiumBooksLimit"
                      name="premiumBooksLimit"
                      type="text"
                      placeholder="Enter premium books limit"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.premiumBooksLimit}
                    />
                    {formik.touched.premiumBooksLimit &&
                      formik.errors.premiumBooksLimit && (
                        <div className={styles.errorText}>
                          {formik.errors.premiumBooksLimit}
                        </div>
                      )}
                  </Grid>

                  <Grid xs={4}>
                    <div className={styles.titleHead}>AlifLaila Commission</div>
                    <input
                      className={styles.input}
                      id="platformFee"
                      name="platformFee"
                      type="text"
                      placeholder="Enter commission %"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.platformFee}
                    />
                    {formik.touched.platformFee &&
                      formik.errors.platformFee && (
                        <div className={styles.errorText}>
                          {formik.errors.platformFee}
                        </div>
                      )}
                  </Grid>
                </Grid>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap="10px"
                  marginTop="32px"
                >
                  <CustomButton
                    text="Cancel"
                    buttonStyle={styles.rejectBtn}
                    handleClick={() => {
                      setIsCreating(false);
                      if (allMous.length) setSelectedMOU(allMous[0]);
                    }}
                  />
                  <CustomButton
                    text="Create MOU"
                    buttonStyle={styles.approveBtn}
                    type="Submit"
                  />
                </Box>
              </form>

              {loader && <Loader />}
            </Card>
          )}

          {/* ── Detail view ──────────────────────────────────────────────── */}
          {!isCreating && selectedInView && (
            <>
              {mouLoader ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="500px"
                  sx={{ borderRadius: "10px" }}
                />
              ) : (
                <Card className={styles.detailCard}>
                  <div className={styles.detailHeader}>
                    <div>
                      <div className={styles.detailTitle}>Create MOU</div>
                      <div className={styles.detailSubTitle}>
                        Signed MOU Contract
                      </div>
                    </div>
                    <div className={styles.detailDate}>
                      Date:{" "}
                      {moment(selectedMOU?.created_at ?? new Date()).format(
                        "D/M/YYYY"
                      )}
                    </div>
                  </div>

                  {/* Agreement text */}
                  <div className={styles.mouHeading}>
                    Agreement Description <span>*</span>
                  </div>
                  {singleLoader ? (
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="250px"
                      sx={{ borderRadius: "20px", marginTop: "10px" }}
                    />
                  ) : (
                    <textarea
                      className={styles.textarea}
                      value={
                        mouDetails?.agreement ||
                        selectedMOU?.agreement ||
                        ""
                      }
                      readOnly
                      placeholder="Agreement description"
                    />
                  )}

                  {/* Three fields */}
                  <Grid container sx={{ marginTop: "20px" }}>
                    <Grid xs={4}>
                      <div className={styles.titleHead}>Free Books Limit</div>
                      {singleLoader ? (
                        <Skeleton
                          variant="rectangular"
                          width="98%"
                          height="45px"
                          sx={{ borderRadius: "32px" }}
                        />
                      ) : (
                        <input
                          className={styles.input}
                          type="text"
                          placeholder="Free books limit"
                          value={mouDetails?.free_book_ratio ?? ""}
                          readOnly
                        />
                      )}
                    </Grid>

                    <Grid xs={4}>
                      <div className={styles.titleHead}>
                        Premium Books Limit
                      </div>
                      {singleLoader ? (
                        <Skeleton
                          variant="rectangular"
                          width="98%"
                          height="45px"
                          sx={{ borderRadius: "32px" }}
                        />
                      ) : (
                        <input
                          className={styles.input}
                          type="text"
                          placeholder="Premium books limit"
                          value={mouDetails?.total_books ?? ""}
                          readOnly
                        />
                      )}
                    </Grid>

                    <Grid xs={4}>
                      <div className={styles.titleHead}>
                        AlifLaila Commission
                      </div>
                      {singleLoader ? (
                        <Skeleton
                          variant="rectangular"
                          width="98%"
                          height="45px"
                          sx={{ borderRadius: "32px" }}
                        />
                      ) : (
                        <input
                          className={styles.input}
                          type="text"
                          placeholder="AlifLaila commission"
                          value={mouDetails?.platform_commission ?? ""}
                          readOnly
                        />
                      )}
                    </Grid>
                  </Grid>

                  {/* Signatures */}
                  <div className={styles.signatureRow}>
                    <div className={styles.sigBlock}>
                      {singleLoader ? (
                        <Skeleton
                          variant="rectangular"
                          width="154px"
                          height="66px"
                        />
                      ) : mouDetails?.admin_signature ? (
                        <img
                          src={mouDetails.admin_signature}
                          className={styles.signatureImg}
                          alt="admin signature"
                        />
                      ) : (
                        <div className={styles.signaturePlaceholder} />
                      )}
                      <div className={styles.signatureLine} />
                      <div className={styles.signerName}>Admin</div>
                    </div>

                    <div className={styles.sigBlock}>
                      {singleLoader ? (
                        <Skeleton
                          variant="rectangular"
                          width="154px"
                          height="66px"
                        />
                      ) : mouDetails?.user_signature ? (
                        <img
                          src={mouDetails.user_signature}
                          className={styles.signatureImg}
                          alt="publisher signature"
                        />
                      ) : (
                        <div className={styles.signaturePlaceholder} />
                      )}
                      <div className={styles.signatureLine} />
                      <div className={styles.signerName}>publisher</div>
                    </div>
                  </div>

                  {/* Footer */}
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap="10px"
                    marginTop="24px"
                  >
                    {selectedMOU?.status === 1 && (
                      <>
                        <CustomButton
                          text="Reject"
                          buttonStyle={styles.rejectBtn}
                          handleClick={() => {
                            setAction({
                              label:
                                "Are you sure you want to reject this Agreement?",
                              type: "reject",
                            });
                            setOpenConfirmation(true);
                          }}
                        />
                        <CustomButton
                          text="Approve & Save"
                          buttonStyle={styles.approveBtn}
                          handleClick={handleApprove}
                        />
                      </>
                    )}
                  </Box>

                  {loader && <Loader />}
                </Card>
              )}
            </>
          )}
        </Grid>
      </Grid>

      <Confirmation
        open={openConfirm}
        handleClose={handleCloseConfirmation}
        handleClick={() => handleActions(action)}
        text={action.label}
      />
    </div>
  );
}

export default PhNewAllMouSignature;
